import { useState } from "react";
import { CodeMirrorComponent } from "../common/code-mirror";
import { useTranslation } from "react-i18next";
import type {
  ScriptAllExamples,
  ScriptExample,
  ScriptExampleType,
} from "~/types/api";
import { Accordion } from "@radix-ui/react-accordion";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Label } from "@radix-ui/react-label";
import useCodeMirrorState from "~/stores/code-mirror";
import { ScriptExampleTypeEnum } from "~/utils/codeMirror-util";

export function ScriptExamples() {
  const { t } = useTranslation();
  const { codeExamples: examples } = useCodeMirrorState();

  const [selectedExample, setSelectedExample] = useState<ScriptExample>({
    name:
      Object.values(examples)?.at(0)?.at(0)?.name ??
      t("scripts.examples.description"),
    type:
      Object.values(examples)?.at(0)?.at(0)?.type ??
      (ScriptExampleTypeEnum.CASE as ScriptExampleType),
    content: Object.values(examples)?.at(0)?.at(0)?.content ?? "",
  });

  return (
    <div className="flex justify-start h-full">
      <ExampleTree
        onSelect={(val) => setSelectedExample(val)}
        examples={examples}
      />
      <div className="ml-2 w-[70%]">
        <div>
          <div>{selectedExample.name}</div>
          <div>
            {t("common.returns")}
            {selectedExample.returns ?? "N/A"}
          </div>
        </div>
        <CodeMirrorComponent
          value={selectedExample.content ?? ""}
          height="60vh"
          notEditable
        />
      </div>
    </div>
  );
}

export function ExampleTree({
  examples,
  onSelect,
}: {
  examples: ScriptAllExamples;
  onSelect: (example: ScriptExample) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="h-[75vh] max-w-[500px] w-[30%] p-3 bg-card rounded-r overflow-x-hidden">
      <Accordion
        type="single"
        collapsible
        className="w-full h-full"
        defaultValue={t(`scripts.examples.types.${ScriptExampleTypeEnum.CASE}`)}
      >
        {Object.entries(examples).map((x) => (
          <ExampleTreeNode
            key={x[0] + "-sc-node"}
            header={t(`scripts.examples.types.${x[0]}`)}
            examples={x[1]}
            onSelect={onSelect}
          />
        ))}
      </Accordion>
    </div>
  );
}

export function ExampleTreeNode({
  examples,
  header,
  onSelect,
}: {
  header: string;
  examples: ScriptExample[];
  onSelect: (example: ScriptExample) => void;
}) {
  return (
    <AccordionItem value={header}>
      <AccordionTrigger>{header}</AccordionTrigger>
      <AccordionContent>
        {examples.map((se: ScriptExample) => (
          <div className="py-2" key={se.name + se.type + "container"}>
            <Label
              htmlFor={header + se.name}
              className="ml-2 hover:cursor-pointer"
              onClick={() => {
                if (se.content != undefined) onSelect(se);
              }}
            >
              {se.name}
            </Label>
            <div>
              {se.description ? (
                <Label
                  htmlFor={header + se.description}
                  className="ml-2 text-sm text-gray-500 hover:cursor-pointer"
                  onClick={() => {
                    if (se.content != undefined) onSelect(se);
                  }}
                >
                  {se.description}
                </Label>
              ) : (
                <></>
              )}
            </div>
          </div>
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
